import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, Container, LinearProgress, IconButton, Fade, useMediaQuery, useTheme, Paper } from '@mui/material';
import { PlayArrow, Pause, VolumeUp, Login as LoginIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AnimatedMascot from './AnimatedMascot';

interface LandingPageProps {
  onGetStarted: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // State for background image with fallback
  const [bgImageSrc, setBgImageSrc] = useState('/landing-bg.jpg');

  // Fade in effect on mount
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Initialize audio
  useEffect(() => {
    console.log('🎵 Initializing audio from: /intro_audio.mp3');
    const audio = new Audio('/intro_audio.mp3');
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      console.log('✅ Audio loaded successfully, duration:', audio.duration);
      setAudioDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setAudioProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleEnded = () => {
      console.log('⏹️ Audio playback ended');
      setIsPlaying(false);
      setAudioProgress(0);
    };

    const handleLoadStart = () => {
      console.log('⏳ Audio loading started...');
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      console.log('✅ Audio can play');
      setIsLoading(false);
    };

    const handleError = (e: Event) => {
      console.error('❌ Audio loading error:', e);
      setIsLoading(false);
      // Removed alert to be less intrusive on the new beautiful landing page
      // alert('Audio file failed to load...'); 
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, []);

  const toggleAudio = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch((error) => {
          console.error('❌ Audio play error:', error);
          setIsPlaying(false);
        });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentTime = () => {
    return audioRef.current ? audioRef.current.currentTime : 0;
  };

  const handleBgError = () => {
    console.warn('⚠️ Local background image not found (/landing-bg.jpg), switching to fallback.');
    setBgImageSrc('https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?q=80&w=2069&auto=format&fit=crop');
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center', // Center the card
      }}
    >
      {/* 1. Background Image */}
      <Box
        component="img"
        src={bgImageSrc}
        alt="Pet Care Background"
        onError={handleBgError}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          transition: 'opacity 0.5s ease-in-out',
        }}
      />

      {/* 2. Gradient Overlay for Readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          background: 'linear-gradient(135deg, rgba(255, 253, 249, 0.2) 0%, rgba(244, 162, 97, 0.1) 100%)',
        }}
      />

      {/* 3. Glassmorphism Card */}
      <Fade in={showContent} timeout={1000}>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            zIndex: 2,
            width: isMobile ? '95%' : 'auto',
            maxWidth: '900px',
            minWidth: isMobile ? 'auto' : '700px',
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            // Golden Hour Glassmorphism Styles
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 20px 60px rgba(38, 70, 83, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxHeight: '95vh',
            overflowY: 'auto',
            // Hide scrollbar but keep functionality
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <img
              src="/PetSnap_logo_whole_transparent.png"
              alt="PetSnap Logo"
              style={{
                height: isMobile ? '50px' : '100px',
                width: 'auto',
                filter: 'drop-shadow(0 4px 12px rgba(244, 162, 97, 0.3))',
                animation: 'softPulse 4s infinite ease-in-out'
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant={isMobile ? 'h5' : 'h2'}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 800,
              color: 'var(--petsnap-forest)',
              fontFamily: "'Outfit', sans-serif",
              mb: { xs: 1, md: 2 },
              letterSpacing: '-0.02em',
            }}
          >
            {t('landing.title')}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant={isMobile ? 'body2' : 'h6'}
            component="p"
            sx={{
              color: 'var(--petsnap-forest)',
              opacity: 0.85,
              mb: { xs: 3, md: 5 },
              maxWidth: 600,
              lineHeight: 1.6,
              fontWeight: 500,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {t('landing.subtitle')}
          </Typography>

          {/* Animated Mascot */}
          <Box
            sx={{
              mb: { xs: 3, md: 4 },
              cursor: 'pointer',
              transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
            onClick={toggleAudio}
          >
            <AnimatedMascot isPlaying={isPlaying} size={isMobile ? 120 : 180} />
          </Box>

          {/* Audio Progress Bar */}
          {(isPlaying || audioProgress > 0) && (
            <Fade in={true}>
              <Box sx={{ width: '100%', maxWidth: 400, mb: { xs: 3, md: 4 } }}>
                <LinearProgress
                  variant="determinate"
                  value={audioProgress}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: 'rgba(38, 70, 83, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                      background: 'var(--gradient-golden)',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--petsnap-forest)', fontWeight: 600, opacity: 0.7 }}>
                    {formatTime(getCurrentTime())}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--petsnap-forest)', fontWeight: 600, opacity: 0.7 }}>
                    {formatTime(audioDuration)}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: isSmallMobile ? 'column' : 'row',
              gap: { xs: 2, md: 3 },
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Listen Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth={isSmallMobile}
              startIcon={isLoading ? <VolumeUp /> : (isPlaying ? <Pause /> : <PlayArrow />)}
              onClick={toggleAudio}
              disabled={isLoading}
              sx={{
                py: 1.8,
                px: 5,
                borderRadius: '12px',
                background: 'var(--gradient-golden)',
                boxShadow: '0 8px 24px rgba(244, 162, 97, 0.25)',
                color: '#FFFFFF',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Outfit', sans-serif",
                '&:hover': {
                  background: 'linear-gradient(135deg, #F4A261 0%, #E76F51 100%)',
                  filter: 'brightness(1.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(244, 162, 97, 0.4)',
                },
              }}
            >
              {isLoading
                ? t('landing.loadingAudio')
                : isPlaying
                  ? t('landing.pauseIntro')
                  : t('landing.listenButton')}
            </Button>

            {/* Get Started Button */}
            <Button
              variant="outlined"
              size="large"
              fullWidth={isSmallMobile}
              startIcon={<LoginIcon />}
              onClick={onGetStarted}
              sx={{
                py: 1.8,
                px: 5,
                borderRadius: '12px',
                borderColor: 'var(--petsnap-honey)',
                borderWidth: '2px',
                color: 'var(--petsnap-forest)',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(10px)',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Outfit', sans-serif",
                '&:hover': {
                  borderColor: 'var(--petsnap-honey)',
                  background: 'rgba(244, 162, 97, 0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {t('landing.getStartedButton')}
            </Button>
          </Box>


          {/* Skip hint - Reduced margin */}
          <Typography
            variant="caption"
            sx={{
              mt: { xs: 2, md: 4 }, // Tighter margin
              color: 'var(--petsnap-forest)',
              opacity: 0.6,
              fontStyle: 'italic',
              fontSize: isMobile ? '0.7rem' : undefined
            }}
          >
            {t('landing.skipHint')}
          </Typography>

        </Paper>
      </Fade>
    </Box>
  );
};

export default LandingPage;
